import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Share2, MessageCircle, Send, Twitter, Download, Loader2 } from "lucide-react";

/**
 * Social-proof share card for a placed Betway parlay.
 * Fetches /api/betway/parlay/{betId}/card.png (JWT-protected) + share links.
 * Shows a PENDING badge immediately; the same endpoint will re-render with
 * "WON" after settlement, so re-opening the modal picks up the winner card
 * without any client-side cache bust tricks (we append ?t=<status>).
 */
export default function ShareCard({ betId }) {
  const [cardSrc, setCardSrc] = useState(null);
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!betId) return;
    (async () => {
      try {
        setLoading(true);
        const [imgRes, linkRes] = await Promise.all([
          axios.get(`${API}/betway/parlay/${betId}/card.png`, { responseType: "blob" }),
          axios.get(`${API}/betway/parlay/${betId}/share`),
        ]);
        if (cancelled) return;
        const url = URL.createObjectURL(imgRes.data);
        setCardSrc(url);
        setLinks(linkRes.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 410
          ? "Share cards aren't generated for lost parlays."
          : "Could not build the share card yet — try again in a few seconds.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [betId]);

  const nativeShare = async () => {
    if (!cardSrc) return;
    try {
      const blob = await (await fetch(cardSrc)).blob();
      const file = new File([blob], `ndakubet-parlay.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My NDAKUBET parlay",
          text: links?.headline || "Placed via NDAKUBET",
        });
      } else {
        // Desktop fallback: open Twitter intent
        window.open(links?.links?.twitter || "#", "_blank", "noopener");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share cancelled");
    }
  };

  const downloadCard = () => {
    if (!cardSrc) return;
    const a = document.createElement("a");
    a.href = cardSrc;
    a.download = `ndakubet-parlay-${betId.slice(0, 8)}.png`;
    a.click();
  };

  if (error) {
    return (
      <div className="rounded-xl border border-border bg-subtle/50 p-3 text-xs text-muted-foreground" data-testid="share-card-error">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-subtle/50 p-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="share-card-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Building your share card…
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-3 space-y-3" data-testid="share-card-block">
      <div className="flex items-center gap-2 text-sm font-display font-semibold">
        <Share2 className="w-4 h-4 text-primary" />
        Share your pick
      </div>

      {cardSrc && (
        <img
          src={cardSrc}
          alt="Parlay share card"
          data-testid="share-card-preview"
          className="w-full rounded-xl border border-border shadow-lg"
          loading="lazy"
        />
      )}

      <div className="grid grid-cols-4 gap-2">
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => window.open(links?.links?.whatsapp || "#", "_blank", "noopener")}
          data-testid="share-whatsapp"
          className="h-9 rounded-full border-border bg-card hover:bg-muted text-foreground"
          title="Share on WhatsApp"
        >
          <MessageCircle className="w-4 h-4 text-success" />
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => window.open(links?.links?.telegram || "#", "_blank", "noopener")}
          data-testid="share-telegram"
          className="h-9 rounded-full border-border bg-card hover:bg-muted text-foreground"
          title="Share on Telegram"
        >
          <Send className="w-4 h-4 text-primary" />
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          onClick={() => window.open(links?.links?.twitter || "#", "_blank", "noopener")}
          data-testid="share-twitter"
          className="h-9 rounded-full border-border bg-card hover:bg-muted text-foreground"
          title="Share on X / Twitter"
        >
          <Twitter className="w-4 h-4" />
        </Button>
        <Button
          type="button" size="sm" variant="outline"
          onClick={nativeShare}
          data-testid="share-native"
          className="h-9 rounded-full border-border bg-card hover:bg-muted text-foreground"
          title="Share… (mobile)"
        >
          <Share2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs">
        <Button
          type="button" size="sm" variant="ghost"
          onClick={downloadCard}
          data-testid="share-download"
          className="h-8 text-muted-foreground hover:text-foreground"
        >
          <Download className="w-3 h-3 mr-1" /> Download PNG
        </Button>
        <span className="text-muted-foreground">Losing parlays never auto-share.</span>
      </div>
    </div>
  );
}
